import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkstationID } from 'hooks/useWorkstationID';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import { dispatchExtensionEvent, focusEvent } from 'domains/extension/extensionEventCreators';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';

export type SubmissionTarget = 'report' | 'addendum';

type WorkstationPromptState = {
  open: boolean;
  value: string;
  error: string | null;
};

type ResolveSubmissionWorkstationIDResult = {
  cancelled: boolean;
  workstationIDToSubmit: string | null;
  requiresPatchBeforeSubmit: boolean;
};

export const useSubmissionWorkstationID = ({
  addendumSMID,
  worklistItem,
}: {
  addendumSMID: Nullable<string>;
  worklistItem: LoadedReportWorklistItem;
}) => {
  const { workstationID, saveWorkstationID } = useWorkstationID();
  const [isPromptEnabled] = useFeatureFlagEnabled(FF.REPORTER_WORKSTATION_ID);
  const [workstationPromptState, setWorkstationPromptState] = useState<WorkstationPromptState>({
    open: false,
    value: '',
    error: null,
  });
  const workstationPromptResolverRef = useRef<((value: string | null) => void) | null>(null);

  /**
   * Resolves the currently displayed workstation-ID prompt, if any.
   *
   * The prompt is modeled as a Promise so submission can pause without threading
   * modal state through every caller in the submit path.
   */
  const resolveWorkstationPrompt = useCallback((value: string | null) => {
    workstationPromptResolverRef.current?.(value);
    workstationPromptResolverRef.current = null;
  }, []);

  const handleWorkstationPromptChange = useCallback((value: string) => {
    setWorkstationPromptState((current) => ({
      ...current,
      value,
      error: null,
    }));
  }, []);

  const handleConfirmWorkstationPrompt = useCallback(() => {
    const saveResult = saveWorkstationID(workstationPromptState.value);

    if (saveResult.ok === false) {
      setWorkstationPromptState((current) => ({
        ...current,
        error: saveResult.error,
      }));
      return;
    }

    setWorkstationPromptState((current) => ({
      ...current,
      open: false,
      value: saveResult.workstationID,
      error: null,
    }));
    resolveWorkstationPrompt(saveResult.workstationID);
  }, [resolveWorkstationPrompt, saveWorkstationID, workstationPromptState.value]);

  const handleCancelWorkstationPrompt = useCallback(() => {
    setWorkstationPromptState((current) => ({
      ...current,
      open: false,
      error: null,
    }));
    resolveWorkstationPrompt(null);
  }, [resolveWorkstationPrompt]);

  /**
   * Opens the submission-time workstation-ID dialog and returns the final user choice.
   *
   * The current browser-local value is used as the starting draft so the prompt can
   * also serve as a correction step when a stale value exists but should be edited.
   */
  const promptForWorkstationID = useCallback((): Promise<string | null> => {
    setWorkstationPromptState({
      open: true,
      value: workstationID ?? '',
      error: null,
    });

    if (isWorkspaceManagerAvailable()) {
      dispatchExtensionEvent(focusEvent());
    } else {
      window.focus();
    }

    return new Promise((resolve) => {
      workstationPromptResolverRef.current = resolve;
    });
  }, [workstationID]);

  /**
   * Centralizes workstation-ID submit gating for both final reports and addendums.
   *
   * It decides whether the current submission may proceed immediately, whether it must
   * pause for user input, or whether the current browser-local value still needs to
   * be patched onto the report/addendum before final submit.
   */
  const resolveSubmissionWorkstationID = useCallback(
    async (target: SubmissionTarget): Promise<ResolveSubmissionWorkstationIDResult> => {
      if (!isPromptEnabled) {
        return {
          cancelled: false,
          workstationIDToSubmit: workstationID,
          requiresPatchBeforeSubmit: false,
        };
      }

      const addendumTargetSmid = target === 'addendum' ? (addendumSMID ?? null) : null;
      const currentAddendum = worklistItem.report?.addendums.find(
        ({ smid }) => smid === addendumTargetSmid
      );
      const workstationIDDiffersFromRemote =
        workstationID != null &&
        ((target === 'report' && workstationID !== worklistItem.report?.workstationID) ||
          (target === 'addendum' &&
            addendumTargetSmid != null &&
            workstationID !== currentAddendum?.workstationID));

      if (workstationID != null) {
        return {
          cancelled: false,
          workstationIDToSubmit: workstationID,
          requiresPatchBeforeSubmit: workstationIDDiffersFromRemote,
        };
      }

      const promptedWorkstationID = await promptForWorkstationID();

      if (promptedWorkstationID == null) {
        return {
          cancelled: true,
          workstationIDToSubmit: workstationID,
          requiresPatchBeforeSubmit: false,
        };
      }

      return {
        cancelled: false,
        workstationIDToSubmit: promptedWorkstationID,
        requiresPatchBeforeSubmit: true,
      };
    },
    [
      addendumSMID,
      isPromptEnabled,
      promptForWorkstationID,
      workstationID,
      worklistItem.report?.addendums,
      worklistItem.report?.workstationID,
    ]
  );

  /**
   * Ensures a pending prompt Promise never outlives this provider. Without this cleanup,
   * unmounting during navigation could leave the submit flow awaiting a resolver that
   * will never fire.
   */
  useEffect(
    () => () => {
      resolveWorkstationPrompt(null);
    },
    [resolveWorkstationPrompt]
  );

  return {
    workstationID,
    resolveSubmissionWorkstationID,
    workstationPromptDialogProps: {
      open: workstationPromptState.open,
      value: workstationPromptState.value,
      error: workstationPromptState.error,
      onChange: handleWorkstationPromptChange,
      onConfirm: handleConfirmWorkstationPrompt,
      onCancel: handleCancelWorkstationPrompt,
    },
  };
};
